import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Customer } from '../../models';
import { ReturnOrder } from '../../models/return-order';
import { CustomerService } from '../customers/customer.service';
import { GungStringConverterService } from '../gung-string-converter.service';

@Injectable({
  providedIn: 'root'
})
export class ReturnOrderService {
  constructor(
    protected http: HttpClient,
    protected customerService: CustomerService
  ) {}

  getReturnOrders(customerId: string, nocache?: boolean): Observable<ReturnOrder[]> {
    const url = `json/return-orders/list/${GungStringConverterService.toGungString(customerId)}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<ReturnOrder[]>(url, { headers }).pipe(
      switchMap(returnOrders => forkJoin([of(returnOrders), this.customerService.getCustomer(customerId).pipe(first())])),
      map(([returnOrders, customer]) => returnOrders.map(ro => this.returnOrderMapper(ro, customer)))
    );
  }

  getAllReturnOrders(nocache?: boolean): Observable<ReturnOrder[]> {
    const url = `json/return-orders`;
    const headers = {
      maxAge: nocache ? '-1' : '60'
    };
    return this.http.get<ReturnOrder[]>(url, { headers }).pipe(
      switchMap(returnOrders => forkJoin([of(returnOrders), this.customerService.getCustomers().pipe(first())])),
      map(([returnOrders, customers]) =>
        returnOrders.map(ro => {
          const customer = customers.filter(c => c.id === GungStringConverterService.fromGungString(ro.deliveryCustomerId))[0];
          return this.returnOrderMapper(ro, customer);
        })
      )
    );
  }

  getReturnOrder(returnOrderId: string, nocache?: boolean): Observable<ReturnOrder> {
    const url = `json/return-orders/${returnOrderId}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<ReturnOrder>(url, { headers }).pipe(
      switchMap(ro =>
        forkJoin([of(ro), this.customerService.getCustomer(ro.deliveryCustomerId).pipe(first())])
      ),
      map(([ro, customer]) => this.returnOrderMapper(ro, customer))
    );
  }

  protected returnOrderMapper(returnOrder: ReturnOrder, customer: Customer): ReturnOrder {
    return {
      ...returnOrder,
      extra: {
        ...returnOrder.extra,
        deliveryCustomer: customer
      }
    };
  }
}
